/* eslint-disable jsx-a11y/alt-text */
import React, { useCallback, useMemo, useState, FC, useContext } from 'react';
import cls from 'classnames';
import { QRCodeSVG } from 'qrcode.react';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import WukongLogo from '@public/images/common/wukong-logo.png';
import { traceEvent } from '@wk/wk-gatherer';
import { copyToClipboard, isMobile } from '@/utils/utils';
import st from '@/components/SimpleToast';
import Title from '@/components/Title';
import Button from '@/components/Button';
import DownloadTablet from '@public/images/download/download-tablet.png';
import DownloadTabletLg from '@public/images/download/download-tablet-lg.png';
import DownloadTabletPhone from '@public/images/download/download-tablet-phone.png';
import DownloadComputer from '@public/images/download/download-computer.png';
import DownloadComputerLg from '@public/images/download/download-computer-lg.png';
import DownloadComputerPhone from '@public/images/download/download-computer-phone.png';
import DownloadPhone from '@public/images/download/download-phone.png';
import DownloadPhoneLg from '@public/images/download/download-phone-lg.png';
import DownloadPhonePhone from '@public/images/download/download-phone-phone.png';
import useIsMediumScreen from '@/hooks/useIsMediumScreen';
import MaxAndMediumScreen from '@/components/Device/MaxAndMediumScreen';
import Mobile from '@/components/Device/Mobile';
import TabBar from './components/TabBar';
import Card from './components/Card';
import DownloadButton from './components/DownloadButton';
import MobileBtn from './components/MobileBtn';
import styles from './styles.module.scss';
import type { HoverQrType, handleGAType, handleGoShopType, DownloadSource } from './data';

type IProps = {
  downloadSource?: DownloadSource;
};

const PagePc: FC<IProps> = ({ downloadSource }) => {
  const { t } = useTranslation('download');
  const [activeTab, setActiveTab] = useState<string>('For Leaners');
  const { handleLogin, student, handleGoToStuCenter } = useContext(GlobalLoginContext);
  const [hoverQrType, setHoverQrType] = useState<HoverQrType>('');
  const isMediumScreen = useIsMediumScreen();
  const { ios, wukongsch, android, redirectUrl } = downloadSource || {};

  const ForTabList = [
    {
      label: t('学生'),
      value: 'For Leaners',
    },
    {
      label: t('家长'),
      value: 'For Parents',
    },
  ];

  const handleGA: handleGAType = useCallback(
    (gaName, extra = {}) => {
      traceEvent({
        data: {
          _event: gaName,
          tab_name: activeTab,
          ...extra,
        },
      });
    },
    [activeTab],
  );

  const handleLoginOrGoToStuCenter = () => {
    handleGA('O_ComputerDownload_StudentCenterClick');
    if (student) {
      handleGoToStuCenter();
    } else {
      handleLogin({
        source: 'STU_CENTER',
      });
    }
  };

  const changeTabBar = (value: string) => {
    setActiveTab(value);
    handleGA('O_Download_PlatformTabClick');
  };

  const handleGoShop: handleGoShopType = useCallback(
    (link, traceName) => {
      handleGA(traceName);
      if (link) {
        window.open(link, '__blank');
      }
    },
    [handleGA],
  );

  const showQR = (value: HoverQrType) => {
    setHoverQrType(value);
    if (value === 'Apple') {
      handleGA('O_APPDownload_AppstoreTransferIn');
    }
    if (value === 'Android') {
      handleGA('O_APPDownload_GoogleplayTransferIn');
    }
  };

  const copyLink = useCallback(
    (link?: string, traceName?: string) => {
      if (!link) return;
      copyToClipboard(link).then(() => {
        st.showToast(t('复制成功'));
      });
      handleGA(traceName);
    },
    [handleGA, t],
  );

  const renderShowImage = useMemo(() => {
    if (hoverQrType === '') {
      let imgDom = (
        <>
          <Mobile>
            <Image alt="" fill src={DownloadPhonePhone} />
          </Mobile>
          <MaxAndMediumScreen>
            <Image alt="" fill src={isMediumScreen ? DownloadPhoneLg : DownloadPhone} />
          </MaxAndMediumScreen>
        </>
      );
      if (activeTab === 'For Leaners') {
        imgDom = (
          <>
            <Mobile>
              <Image alt="" fill src={DownloadTabletPhone} />
            </Mobile>
            <MaxAndMediumScreen>
              <Image alt="" fill src={isMediumScreen ? DownloadTabletLg : DownloadTablet} />
            </MaxAndMediumScreen>
          </>
        );
      }
      return imgDom;
    }
    return (
      <div
        className={cls(
          styles.gradientBg,
          'xl-p-3 relative rounded-5xl p-[10px] lg:top-[-15px] lg:h-[220px] lg:w-[220px] xl:top-[-32px] xl:h-[280px] xl:w-[280px]',
        )}
      >
        <div className="flex h-full w-full items-center justify-center rounded-[31px] bg-white">
          <QRCodeSVG
            className="lg:w-[170px] xl:w-[220px]"
            value={redirectUrl || ''}
            imageSettings={{
              src: WukongLogo.src,
              width: 52,
              height: 52,
              excavate: true,
            }}
            level="Q"
            size={220}
          />
        </div>
      </div>
    );
  }, [hoverQrType, redirectUrl, isMediumScreen, activeTab]);

  return (
    <div className="mb-16 p-5 lg:mb-56 xl:mb-20">
      <div className="mx-auto flex flex-col items-center justify-center lg:w-[984px] xl:mt-[115px] xl:w-[1400px]">
        <Image
          className="absolute top-[150px]"
          alt="WuKong APP"
          width={800}
          height={400}
          src="/images/home/blog-bg.png"
          quality={100}
        />
        <div className="relative flex flex-col items-center">
          <Title className="mb-6 mt-6 md:mb-12 md:mt-15 lg:mb-8 lg:mt-12" theme="colorful">
            <strong>{t('下载 WuKong APP')}</strong>
          </Title>
          <TabBar onChange={changeTabBar} tabList={ForTabList} activeTab={activeTab} />
        </div>
        <div className="relative grid w-full grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8 xl:gap-12">
          <Card
            gradientBg={hoverQrType === ''}
            showTitle={hoverQrType === ''}
            title={activeTab === 'For Leaners' ? t('平板') : t('手机')}
            cover={renderShowImage}
            commandWord={t('推荐')}
            showCommand={hoverQrType === ''}
          >
            <MaxAndMediumScreen>
              <div className="grid grid-cols-2 lg:mx-[32px] lg:gap-4 lg:pt-8 xl:mx-[64px] xl:gap-6 xl:pt-12">
                <DownloadButton
                  onClick={() => handleGoShop(ios, 'O_APPDownload_AppleStoreClick')}
                  showQR={!isMobile()}
                  onHoverQr={(visible) => showQR(visible ? 'Apple' : '')}
                  type="Apple"
                />
                <DownloadButton
                  onClick={() => handleGoShop(android, 'O_APPDownload_AndroidGooglePlayClick')}
                  showQR={!isMobile()}
                  onHoverQr={(visible) => showQR(visible ? 'Android' : '')}
                  type="Android"
                />
              </div>
            </MaxAndMediumScreen>
            <Mobile>
              <div className="w-full px-5">
                <MobileBtn downloadSource={downloadSource} onClick={handleGoShop} handleGA={handleGA} />
              </div>
            </Mobile>
          </Card>
          <Card
            title={t('电脑')}
            cover={
              <>
                <Mobile>
                  <Image alt={t('电脑')} fill src={DownloadComputerPhone} quality={100} />
                </Mobile>
                <MaxAndMediumScreen>
                  <Image
                    alt={t('电脑')}
                    fill
                    src={isMediumScreen ? DownloadComputerLg : DownloadComputer}
                    quality={100}
                  />
                </MaxAndMediumScreen>
              </>
            }
          >
            <MaxAndMediumScreen>
              <div className=" flex flex-col items-center justify-center lg:mt-3 xl:mt-10">
                <Button onClick={handleLoginOrGoToStuCenter} className="z-0" theme="colorfulBorder" size="xl">
                  {t('立即访问')}
                </Button>
                <div className="flex flex-wrap justify-center text-center lg:mt-6 lg:text-[14px] xl:mt-9 xl:text-[16px]">
                  {t('请使用Google Chrome浏览器访问，')}
                  <a
                    className={styles.gradientA}
                    // eslint-disable-next-line no-template-curly-in-string
                    href={downloadSource?.google || ''}
                    onClick={() => {
                      handleGA('O_ComputerDownload_ChromeClick');
                    }}
                    target="_blank"
                  >
                    {t('点击下载Google Chrome浏览器')}
                  </a>
                </div>
              </div>
            </MaxAndMediumScreen>
            <Mobile>
              <div className="flex flex-col items-center px-5 pb-5 text-[16px]">
                <a
                  className="mb-2 text-xl font-bold"
                  onClick={() => copyLink(wukongsch, 'O_APPDownload_Copylink_Click')}
                >
                  {wukongsch?.slice(0, -1)}
                </a>
                {t('点击按钮复制链接，在电脑端打开')}
                <Button
                  theme="colorfulBorder"
                  size="xl"
                  className="mt-5 w-full"
                  onClick={() => copyLink(wukongsch, 'O_APPDownload_Copylink_Click')}
                >
                  {t('复制链接')}
                </Button>
              </div>
            </Mobile>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PagePc;
